
import React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { Slash } from "lucide-react";
import {
  Settings,
  Printer,
  Wrench,
  Zap,
  ShoppingCart
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const requirementIconVariants = cva(
  "relative cursor-pointer transition-colors duration-200",
  {
    variants: {
      type: {
        root: "text-destructive hover:text-destructive/80",
        tools: "text-purple-600 dark:text-[#D946EF] hover:text-purple-600/80 dark:hover:text-[#D946EF]/80",
        wiring: "text-yellow-400 hover:text-yellow-400/80",
        printing: "text-blue-500 dark:text-[#0EA5E9] hover:text-blue-500/80 dark:hover:text-[#0EA5E9]/80",
        purchase: "text-lime-600 dark:text-green-400 hover:text-lime-600/80 dark:hover:text-green-400/80"
      },
      state: {
        active: "",
        inactive: "text-muted-foreground requirement-inactive",
        disabled: "opacity-30 cursor-not-allowed"
      },
      size: {
        sm: "mx-0.5",
        md: "mx-1",
        lg: "mx-1.5"
      }
    },
    defaultVariants: {
      type: "root",
      state: "active",
      size: "sm"
    }
  }
);

interface RequirementIconProps extends VariantProps<typeof requirementIconVariants> {
  active?: boolean;
  disabled?: boolean;
  showStrikethrough?: boolean;
  iconSize?: number;
  tooltip?: string;
  onClick?: () => void;
  className?: string;
  "aria-label"?: string;
}

const iconMap = {
  root: Settings,
  tools: Wrench,
  wiring: Zap,
  printing: Printer,
  purchase: ShoppingCart
};

const RequirementIcon = React.forwardRef<HTMLSpanElement, RequirementIconProps>(
  ({
    type = "root",
    size,
    state,
    active = true,
    disabled = false,
    showStrikethrough = false,
    iconSize = 16,
    tooltip,
    onClick,
    className,
    "aria-label": ariaLabel,
    ...props
  }, ref) => {
    const IconComponent = iconMap[type];
    const currentState = disabled ? "disabled" : !active ? "inactive" : "active";
    const showSlash = showStrikethrough && !active;

    const IconContent = (
      <span
        ref={ref}
        className={cn(
          requirementIconVariants({ type, size, state: currentState }),
          className
        )}
        onClick={disabled ? undefined : onClick}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={ariaLabel || `${type} requirement`}
        aria-pressed={active}
        aria-disabled={disabled}
        onKeyDown={(e) => {
          if (!disabled && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            onClick?.();
          }
        }}
        {...props}
      >
        <IconComponent size={iconSize} />
        {showSlash && (
          <div className="absolute inset-0 flex items-center justify-center text-muted-foreground requirement-inactive rotate-90">
            <Slash size={iconSize} className="stroke-[2.5]" />
          </div>
        )}
      </span>
    );

    if (tooltip) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              {IconContent}
            </TooltipTrigger>
            <TooltipContent>
              <p>{tooltip}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return IconContent;
  }
);

RequirementIcon.displayName = "RequirementIcon";

export { RequirementIcon, requirementIconVariants };
export type { RequirementIconProps };
